import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from "react-router-dom";
import { signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { auth, database } from '../firebase/firebaseConfig';
import { Helmet } from 'react-helmet-async';
import NavBar from '../components/NavBar';
import { get, ref } from 'firebase/database';

const ShopLogin = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const navigate = useNavigate();
    const [isLoging, setIsLoging] = useState(false);
    const location = useLocation();

    const searchParams = new URLSearchParams(location.search);
    const accountCreation = searchParams.get("success") === "true";
    const reviewEmailFailed = searchParams.get("reviewEmail") === "failed";
    const blockedStatus = searchParams.get("status");
    const [showAccountCreatedPopup, setShowAccountCreatedPopup] = useState(false);


    useEffect(() => {
        if (accountCreation) {
            setShowAccountCreatedPopup(true);

            const params = new URLSearchParams(location.search);
            params.delete("success");

            navigate(
                `${location.pathname}${params.toString() ? `?${params.toString()}` : ""}`,
                { replace: true }
            );
        }
    }, [accountCreation, navigate, location]);

    useEffect(() => {
        if (reviewEmailFailed) {
            setError("Your account is under review. We could not send the confirmation email, so please contact support.");
        }
        if (blockedStatus === "rejected") {
            setError("Your account application was rejected. Please contact support for more information.");
        } else if (blockedStatus === "review") {
            setError("Your account is under review. You will be able to sign in after it is approved.");
        }
    }, [reviewEmailFailed, blockedStatus]);


    const handleLogin = async (e) => {
        e.preventDefault();
        setIsLoging(true);
        setError('');

        try {
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;

            // 🔥 GET USER FROM REALTIME DATABASE
            const userRef = ref(database, 'Users/' + user.uid);
            const snapshot = await get(userRef);

            if (!snapshot.exists()) {
                setError('No account data found!');
                return;
            }

            const userData = snapshot.val();

            // 🔐 check active status
            if (["printShop", "supplier", "specialist"].includes(userData.type) && userData.status !== "approved") {
                await signOut(auth);
                setError(userData.status === "rejected"
                    ? "Your account application was rejected. Please contact support for more information."
                    : "Your account is under review. You will be able to sign in after it is approved.");
                return;
            }

            // 🔥 TYPE BASED REDIRECTION
            const type = userData?.type;

            setTimeout(() => {
                if (type === 'printShop') {
                    navigate("/shop/dashboard", { replace: true });
                }
                else if (type === 'supplier' || type === 'specialist') {
                    navigate("/supplier/dashboard", { replace: true });
                }
                else if (type === 'admin') {
                    navigate("/admin/dashboard", { replace: true });
                }
                else {
                    setError("Invalid account type!");
                }

            }, 1000); // Simulate loading time

        } catch (err) {
            if (err.code === 'auth/user-not-found') {
                setError('Email not found. Please check again.');
            } else if (err.code === 'auth/invalid-credential') {
                setError('Incorrect password.');
            } else {
                console.log(err.code);
                setError('Login failed. Please try again.');
            }
        } finally {
            setIsLoging(false);
        }
    };

    return (
        <div className="min-h-full bg-[#f8f8fa] flex flex-col font-['Plus_Jakarta_Sans',sans-serif]">
            <Helmet>
                <title>Store Login - Signet Print</title>
                <meta
                    name="description"
                    content="Login to your store account on Signet Print."
                />
            </Helmet>
            <NavBar isLoging={true} />
            <main className="flex-1 flex items-center justify-center p-[16px] md:p-[40px]">

                <div className="w-full max-w-[420px]">

                    {/* HEADER */}
                    <div className="text-center mb-[32px]">
                        <h1 className="font-semibold text-[28px] text-[#212326] mb-[8px]">
                            Store Login
                        </h1>

                        <p className="font-medium text-[15px] text-[#717182] leading-[1.6]">
                            Sign in to manage your shop, view orders, and update your profile.
                        </p>
                    </div>

                    {/* FORM */}
                    <form
                        onSubmit={handleLogin}
                        className="bg-white rounded-[16px] border border-[#e4e5e7] p-[24px] md:p-[32px] flex flex-col gap-[20px]"
                    >

                        {/* ERROR */}
                        {
                            error && (
                                <div className="bg-[#fff0f0] border border-[#ffcdd2] rounded-[10px] px-[16px] py-[12px]">
                                    <p className="font-medium text-[13px] text-[#d32f2f]">
                                        {error || "An error occurred while creating your account. Please try again."}
                                    </p>
                                </div>
                            )
                        }

                        {/* EMAIL */}
                        <div className="flex flex-col gap-[6px]">
                            <label className="font-semibold text-[14px] text-[#212326]">
                                Email
                            </label>

                            <div className="relative">
                                <svg className="absolute left-[14px] top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-[#9298a0]"
                                    xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                                    stroke="currentColor" strokeWidth="2">
                                    <rect width="20" height="16" x="2" y="4" rx="2" />
                                    <path d="m22 7-10 5L2 7" />
                                </svg>

                                <input
                                    type="email"
                                    placeholder="shop@email.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    className="w-full h-[48px] rounded-[12px] border border-[#e4e5e7] font-semibold pl-[40px] pr-[16px] text-[14px] text-[#212326] placeholder:text-[#9298a0] focus:outline-none focus:border-[#ff5323]"
                                />
                            </div>
                        </div>

                        {/* PASSWORD */}
                        <div className="flex flex-col gap-[6px]">
                            <div className="flex items-center justify-between">
                                <label className="font-semibold text-[14px] text-[#212326]">
                                    Password
                                </label>

                                <a
                                    href="/forgot-password"
                                    className="font-medium text-[13px] text-[#ff5323] hover:underline"
                                >
                                    Forgot password?
                                </a>
                            </div>

                            <div className="relative">
                                <svg className="absolute left-[14px] top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-[#9298a0]"
                                    xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                                    stroke="currentColor" strokeWidth="2">
                                    <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
                                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                </svg>

                                <input
                                    type={showPassword ? "text" : "password"}
                                    placeholder="Your password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    className="w-full h-[48px] rounded-[12px] border border-[#e4e5e7] font-semibold pl-[40px] pr-[44px] text-[14px] text-[#212326] placeholder:text-[#9298a0] focus:outline-none focus:border-[#ff5323]"
                                />

                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-[14px] top-1/2 -translate-y-1/2 text-[#9298a0] hover:text-[#212326]"
                                >
                                    {showPassword ? (
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                            stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-eye-off w-[18px] h-[18px]">
                                            <path d="M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49"></path>
                                            <path d="M14.084 14.158a3 3 0 0 1-4.242-4.242"></path>
                                            <path d="M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143"></path>
                                            <path d="m2 2 20 20"></path>
                                        </svg>
                                    ) : (
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                            stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-eye w-[18px] h-[18px]">
                                            <path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"></path>
                                            <circle cx="12" cy="12" r="3"></circle>
                                        </svg>
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* SUBMIT */}
                        <button
                            type="submit"
                            disabled={isLoging}
                            className="flex items-center justify-center gap-[8px] w-full h-[48px] rounded-[12px] font-semibold text-[15px] bg-[#ff5323] text-white hover:bg-[#e64a1f] shadow-[0px_2px_8px_rgba(255,83,35,0.25)]"
                        >
                            {isLoging ? "Signing In..." : "Sign In"}

                            {!isLoging && (
                                <svg className="w-[16px] h-[16px]"
                                    xmlns="http://www.w3.org/2000/svg" fill="none"
                                    viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                    <path d="M5 12h14" />
                                    <path d="m12 5 7 7-7 7" />
                                </svg>
                            )}
                        </button>

                        {/* BACK LINK STYLE */}
                        <p className="text-center font-medium text-[13px] text-[#9298a0]">
                            Don't have an account?{" "}
                            <a href="/onboarding/plan" className="text-[#ff5323] font-semibold hover:underline">
                                Get started
                            </a>
                        </p>

                    </form>
                </div>
            </main>

            {showAccountCreatedPopup && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
                    <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">

                        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-orange-100">
                            <svg
                                className="h-7 w-7 text-[#ff5323]"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth="2"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M12 8v4m0 4h.01M22 12A10 10 0 1112 2a10 10 0 0110 10z"
                                />
                            </svg>
                        </div>

                        <h2 className="text-center text-xl font-semibold text-[#212326]">
                            Account Submitted
                        </h2>

                        <p className="mt-4 text-center text-[15px] leading-7 text-[#717182]">
                            Your account has been submitted successfully and is currently
                            <strong> under review.</strong>
                        </p>

                        <p className="mt-3 text-center text-[15px] leading-7 text-[#717182]">
                            Please check your <strong>Inbox</strong> and <strong>Spam</strong> folder for our approval email.
                            Once your account has been approved, you'll receive an email and can then sign in to your shop account.
                        </p>

                        <button
                            onClick={() => setShowAccountCreatedPopup(false)}
                            className="mt-6 h-12 w-full rounded-xl bg-[#ff5323] font-semibold text-white hover:bg-[#e64a1f]"
                        >
                            Got it
                        </button>

                    </div>
                </div>
            )}
        </div>
    )
}

export default ShopLogin;
